import React from 'react';
import {
  Gauge, ClipboardCheck, Wrench, FileText, AlertCircle,
  Settings, Zap, Shield, TrendingUp, Activity, Eye, Target,
} from 'lucide-react';
import ServicePage from '../../components/enterprise/ServicePage';

const OfflineMeasurementPage = () => (
  <ServicePage
    hero={{
      eyebrow: 'Offline Measurement',
      title: (
        <>
          Precision offline measurement <em>&amp; testing services</em>
        </>
      ),
      lede: 'Ensure optimal performance and compliance with professional offline measurement and testing services. Our comprehensive measurement solutions provide accurate data for renewable energy assets.',
      image: '/assets/img/general/photovoltaic-4541308_1280.jpg',
      imageAlt: 'Field testing at a photovoltaic site',
      actions: [
        { label: 'Request a test plan', to: '/contact' },
        { label: 'Explore all services', to: '/service' },
      ],
      metrics: [
        { value: '±0.1%', label: 'Measurement accuracy' },
        { value: '100+', label: 'Test parameters' },
        { value: 'Certified', label: 'Equipment' },
        { value: '24/7', label: 'Service available' },
      ],
    }}
    overview={{
      eyebrow: 'Overview',
      title: 'Professional testing services',
      lede: 'Our offline measurement services provide comprehensive testing and verification for renewable energy assets with industry-leading accuracy.',
      body: [
        'Electrical, mechanical, thermal and environmental measurements are captured with calibrated instruments and returned as documented evidence — usable for warranty claims, compliance filings and performance disputes alike.',
      ],
      checklist: [
        'Electrical parameter measurements and testing',
        'Mechanical performance assessments',
        'Environmental condition monitoring',
        'Equipment calibration and verification',
        'Power quality analysis and measurements',
        'Thermal imaging and temperature profiling',
        'Vibration analysis and monitoring',
        'Comprehensive testing and documentation',
      ],
      image: '/assets/img/general/solar-6816101_1280.jpg',
      imageAlt: 'Solar installation undergoing measurement',
      imageCaption: 'Professional offline measurement services with certified equipment and expertise',
    }}
    capabilities={{
      eyebrow: 'Service features',
      title: 'Comprehensive measurement services',
      lede: 'Professional testing and measurement capabilities for all renewable energy assets.',
      items: [
        {
          icon: Gauge,
          title: 'Precision measurement',
          description: 'High-accuracy offline measurements for equipment calibration and performance verification.',
        },
        {
          icon: ClipboardCheck,
          title: 'Field testing',
          description: 'Comprehensive field testing and measurement services for renewable energy assets.',
        },
        {
          icon: Wrench,
          title: 'Equipment calibration',
          description: 'Professional calibration services ensuring accurate measurements and optimal performance.',
        },
        {
          icon: FileText,
          title: 'Detailed reporting',
          description: 'Comprehensive measurement reports with analysis and recommendations.',
        },
      ],
    }}
    coverage={{
      eyebrow: 'Measurement types',
      title: 'Complete testing coverage',
      lede: 'Comprehensive measurement services for all critical parameters.',
      items: [
        {
          icon: Zap,
          title: 'Electrical measurements',
          description: 'Comprehensive electrical testing including voltage, current, power, and resistance measurements.',
        },
        {
          icon: Settings,
          title: 'Mechanical testing',
          description: 'Torque, alignment, vibration, and other mechanical parameter measurements.',
        },
        {
          icon: Activity,
          title: 'Performance verification',
          description: 'System and equipment performance verification against specifications.',
        },
        {
          icon: Eye,
          title: 'Quality testing',
          description: 'Comprehensive quality assurance testing and compliance verification.',
        },
      ],
    }}
    outcomes={{
      eyebrow: 'Key benefits',
      title: 'Ensure optimal performance',
      lede: 'Achieve superior results with precision measurement services.',
      items: [
        {
          icon: Target,
          title: 'Accurate data',
          description: 'Obtain highly accurate measurement data for informed decision-making.',
        },
        {
          icon: Shield,
          title: 'Quality assurance',
          description: 'Ensure equipment and systems meet performance and safety standards.',
        },
        {
          icon: TrendingUp,
          title: 'Optimize performance',
          description: 'Identify and address performance gaps based on precise measurements.',
        },
        {
          icon: AlertCircle,
          title: 'Early detection',
          description: 'Detect potential issues before they impact operations or safety.',
        },
      ],
    }}
    cta={{
      title: 'Need professional testing?',
      body: 'Get precision offline measurement services for your renewable energy assets.',
      primary: { label: 'Talk to our team', to: '/contact' },
      secondary: { label: 'View all services', to: '/service' },
    }}
  />
);

export default OfflineMeasurementPage;
